import { BaseVisualizer } from './BaseVisualizer';

/**
 * Particle for trail effects
 */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  hue: number;
  size: number;
}

/**
 * Energy ring for dynamic expanding effect
 */
interface Ring {
  radius: number;           // Current radius
  maxRadius: number;        // Maximum radius before removal
  speed: number;            // Expansion speed per frame
  thickness: number;        // Line thickness
  opacity: number;          // Current opacity (0-1)
  hue: number;             // Ring color hue
  saturation: number;      // Color saturation
  lightness: number;       // Color lightness
  spawnTime: number;       // Time when ring was created
}

/**
 * Psychedelic circular visualizer with trippy, organic patterns
 * Features:
 * - Multi-layered kaleidoscope patterns
 * - Color cycling based on music data
 * - Particle trails
 * - Beat-reactive pulses
 * - Mandala-like geometric patterns
 * - Smooth flowing animations
 */
export class PsychedelicVisualizer extends BaseVisualizer {
  private particles: Particle[] = [];
  private maxParticles: number = 100;
  private hueOffset: number = 0;
  private pulsePhase: number = 0;
  private kaleidoscopeSegments: number = 6;
  private rotationOffset: number = 0;
  
  // Dynamic energy rings system
  private rings: Ring[] = [];
  private ringSpawnCooldown: number = 0; // Frames to wait before spawning next ring
  private frameCount: number = 0; // Track frames for periodic spawning
  
  // Intro animation system
  private visualizerState: 'idle' | 'intro' | 'running' = 'idle';
  private introStartTime: number = 0;
  private introDuration: number = 2.5; // seconds
  private hasStartedOnce: boolean = false;
  
  constructor(canvas: HTMLCanvasElement) {
    super(canvas, 180);
  }

  protected render(): void {
    const { x: centerX, y: centerY } = this.getCenter();
    
    // Handle different states
    if (this.visualizerState === 'idle') {
      this.renderIdleState(centerX, centerY);
      return;
    }
    
    // Create trippy background with fade trail effect
    this.ctx.fillStyle = 'rgba(0, 0, 5, 0.15)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Update animation parameters
    this.updateAnimationState();
    
    // Get intro progress if in intro state
    const introProgress = this.getIntroProgress();
    const isInIntro = this.isInIntro();
    
    // Apply global opacity during intro
    if (isInIntro) {
      this.ctx.save();
      this.ctx.globalAlpha = this.easeOutCubic(introProgress);
    }
    
    // Layer 1: Outer rotating mandala (appears at 40% intro)
    if (!isInIntro || introProgress > 0.4) {
      const mandalaScale = isInIntro ? this.easeOutBack(Math.max(0, (introProgress - 0.4) / 0.6)) : 1;
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) * mandalaScale : 1;
      this.drawRotatingMandala(centerX, centerY, 1.2, 12, this.rotationOffset);
    }
    
    // Layer 2: Middle frequency bars (appears at 60% intro)
    if (!isInIntro || introProgress > 0.6) {
      const circleScale = isInIntro ? this.easeOutBack(Math.max(0, (introProgress - 0.6) / 0.4)) : 1;
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) * circleScale : 1;
      this.drawFrequencyCircle(centerX, centerY, 1.0);
    }
    
    // Layer 3: Inner kaleidoscope pattern (appears at 50% intro)
    if (!isInIntro || introProgress > 0.5) {
      const kaleScale = isInIntro ? this.easeOutBack(Math.max(0, (introProgress - 0.5) / 0.5)) : 1;
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) * kaleScale : 1;
      this.drawKaleidoscope(centerX, centerY, 0.5);
    }
    
    // Layer 4: Particle system (appears at 70% intro)
    if (!isInIntro || introProgress > 0.7) {
      const particleScale = isInIntro ? this.easeOutCubic(Math.max(0, (introProgress - 0.7) / 0.3)) : 1;
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) * particleScale : 1;
      this.updateAndDrawParticles();
    }
    
    // Layer 5: Center orb with beat pulse (appears first at 0% intro)
    this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) : 1;
    const orbScale = isInIntro ? this.easeOutBack(introProgress) : 1;
    this.drawCenterOrb(centerX, centerY, orbScale);
    
    // Layer 6: Beat and bar flash overlays
    if (this.currentBeat > 0.7) {
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) : 1;
      this.drawBeatFlash();
    }
    if (this.currentBar > 0.75) {
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) : 1;
      this.drawBarFlash();
    }
    
    // Layer 7: Flowing energy rings (appears at 20% intro)
    if (!isInIntro || introProgress > 0.2) {
      const ringScale = isInIntro ? this.easeOutCubic(Math.max(0, (introProgress - 0.2) / 0.8)) : 1;
      this.ctx.globalAlpha = isInIntro ? this.easeOutCubic(introProgress) * ringScale : 1;
      this.drawEnergyRings(centerX, centerY);
    }
    
    // Restore context if we modified it for intro
    if (isInIntro) {
      this.ctx.restore();
    }
    
    // Check if intro is complete
    if (isInIntro && introProgress >= 1) {
      this.visualizerState = 'running';
    }
  }

  /**
   * Update animation state parameters
   */
  private updateAnimationState(): void {
    // Increment frame counter
    this.frameCount++;
    
    // Hue cycles through full spectrum
    this.hueOffset = (this.hueOffset + this.currentEnergy * 2 + 0.5) % 360;
    
    // Rotation speed affected by energy
    this.rotationOffset += (this.currentEnergy * 0.01 + 0.002);
    
    // Pulse phase for center orb
    this.pulsePhase += 0.1 + this.currentEnergy * 0.1;
    
    // Spawn particles on beats
    if (this.currentBeat > 0.6) {
      this.spawnBeatParticles();
    }
    
    // Update dynamic ring spawning and management
    this.updateEnergyRings();
  }

  /**
   * Draw outer rotating mandala pattern
   */
  private drawRotatingMandala(
    centerX: number,
    centerY: number,
    radiusScale: number,
    petals: number,
    rotation: number
  ): void {
    const radius = this.getBaseRadius() * radiusScale;
    
    this.ctx.save();
    this.ctx.translate(centerX, centerY);
    this.ctx.rotate(rotation);
    
    // Draw symmetrical petals
    for (let i = 0; i < petals; i++) {
      const angle = (Math.PI * 2 * i) / petals;
      
      // Petal intensity from pitch data
      const pitchIndex = this.currentPitches.length > 0 
        ? i % this.currentPitches.length 
        : i % 12;
      const intensity = this.currentPitches[pitchIndex] || 0.5;
      
      // Radius affected by energy and beat
      const dynamicRadius = radius * (1 + this.currentBeat * 0.1);
      const petalRadius = dynamicRadius * 0.3 * (0.9 + intensity * 0.2);
      
      const x = Math.cos(angle) * dynamicRadius;
      const y = Math.sin(angle) * dynamicRadius;
      
      // Create gradient petal with enhanced intensity
      const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, petalRadius);
      const hue = (this.hueOffset + i * (360 / petals)) % 360;
      const alpha = 0.5 + intensity * 0.3 + this.currentEnergy * 0.2;
      gradient.addColorStop(0, this.hsl(hue, 100, 60, alpha));
      gradient.addColorStop(0.5, this.hsl(hue, 100, 50, alpha * 0.6));
      gradient.addColorStop(1, this.hsl(hue, 100, 30, 0));
      
      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(x, y, petalRadius * (0.8 + intensity * 0.4), 0, Math.PI * 2);
      this.ctx.fill();
      
      // Draw connecting lines with beat response
      const lineAlpha = 0.15 + this.currentBeat * 0.15 + intensity * 0.1;
      this.ctx.strokeStyle = this.hsl(hue, 80, 50, lineAlpha);
      this.ctx.lineWidth = 1 + this.currentLoudness * 2;
      this.ctx.beginPath();
      this.ctx.moveTo(0, 0);
      this.ctx.lineTo(x, y);
      this.ctx.stroke();
    }
    
    this.ctx.restore();
  }

  /**
   * Draw main frequency circle with bars
   */
  private drawFrequencyCircle(centerX: number, centerY: number, radiusScale: number): void {
    const radius = this.getBaseRadius() * radiusScale;
    const innerRadius = radius * 0.4;
    const bars = this.simulatedData.length;
    
    // Enable glow effect
    this.ctx.shadowBlur = 15 + this.currentBeat * 20;
    
    for (let i = 0; i < bars; i++) {
      const rads = (Math.PI * 2 * i) / bars - Math.PI / 2;
      const barHeight = (this.simulatedData[i] / 255) * radius * 0.8;
      
      // Start and end points
      const x1 = centerX + Math.cos(rads) * innerRadius;
      const y1 = centerY + Math.sin(rads) * innerRadius;
      const x2 = centerX + Math.cos(rads) * (innerRadius + barHeight);
      const y2 = centerY + Math.sin(rads) * (innerRadius + barHeight);
      
      // Color based on frequency position and pitch
      const pitchIndex = Math.floor((i / bars) * 12) % 12;
      const pitchValue = this.currentPitches[pitchIndex] || 0.5;
      const hue = (this.hueOffset + pitchIndex * 30 + (i / bars) * 120) % 360;
      const saturation = 85 + pitchValue * 15;
      const lightness = 45 + (barHeight / (radius * 0.8)) * 30 + this.currentBeat * 15;
      
      this.ctx.strokeStyle = this.hsl(hue, saturation, lightness, 0.9);
      this.ctx.shadowColor = this.hsl(hue, 100, 60, 0.8);
      this.ctx.lineWidth = Math.max(2, (this.canvas.width / bars) * 1.2);
      
      this.ctx.beginPath();
      this.ctx.moveTo(x1, y1);
      this.ctx.lineTo(x2, y2);
      this.ctx.stroke();
    }
    
    this.ctx.shadowBlur = 0;
  }

  /**
   * Draw kaleidoscope pattern in the center
   */
  private drawKaleidoscope(centerX: number, centerY: number, radiusScale: number): void {
    const radius = this.getBaseRadius() * radiusScale;
    
    this.ctx.save();
    this.ctx.translate(centerX, centerY);
    
    // Draw multiple segments with mirroring
    for (let seg = 0; seg < this.kaleidoscopeSegments; seg++) {
      this.ctx.save();
      this.ctx.rotate((Math.PI * 2 * seg) / this.kaleidoscopeSegments - this.rotationOffset * 2);
      
      // Draw patterns within each segment - more dots for high energy
      const numDots = Math.floor(20 + this.currentEnergy * 10);
      for (let i = 0; i < numDots; i++) {
        const t = i / numDots;
        const r = radius * t * (0.9 + this.currentBar * 0.2); // Bar affects radius
        const angle = t * Math.PI * 4 + this.currentTime * 2 + this.currentBeat * 0.5;
        
        const x = Math.cos(angle) * r;
        const y = Math.sin(angle) * r;
        
        // Size affected by energy, bar, and beat
        const baseSize = 2 + this.currentEnergy * 4 + this.currentBar * 3;
        const beatPulse = this.currentBeat > 0.7 ? this.currentBeat * 2 : 0;
        const size = baseSize + beatPulse;
        
        // Color from pitch data
        const pitchIndex = Math.floor(t * 12) % 12;
        const pitchValue = this.currentPitches[pitchIndex] || 0.5;
        const hue = (this.hueOffset + t * 180 + seg * 60) % 360;
        
        // Alpha affected by pitch and loudness
        const alpha = 0.5 + pitchValue * 0.3 + this.currentLoudness * 0.2;
        
        this.ctx.fillStyle = this.hsl(hue, 90, 55, alpha);
        this.ctx.beginPath();
        this.ctx.arc(x, y, size, 0, Math.PI * 2);
        this.ctx.fill();
      }
      
      this.ctx.restore();
    }
    
    this.ctx.restore();
  }

  /**
   * Update energy rings system - spawn, expand, and remove rings
   */
  private updateEnergyRings(): void {
    const baseRadius = this.getBaseRadius();
    const maxRadiusThreshold = Math.max(this.canvas.width, this.canvas.height) * 0.7;
    
    // Update existing rings
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      
      // Expand ring based on its speed (which is energy-dependent)
      ring.radius += ring.speed;
      
      // Fade opacity as ring expands
      const expansionProgress = ring.radius / ring.maxRadius;
      ring.opacity = Math.max(0, 1 - expansionProgress);
      
      // Remove rings that have fully expanded
      if (ring.radius >= ring.maxRadius || ring.opacity <= 0.05) {
        this.rings.splice(i, 1);
      }
    }
    
    // Decrease spawn cooldown
    if (this.ringSpawnCooldown > 0) {
      this.ringSpawnCooldown--;
    }
    
    // Dynamic max rings based on energy and loudness (3 to 35 rings)
    const dynamicMaxRings = Math.floor(3 + this.currentEnergy * 22 + this.currentLoudness * 10);
    
    // Determine MINIMUM desired rings based on energy level (not random!)
    // This ensures continuous spawning without random fluctuations blocking it
    let minDesiredRings = 0;
    if (this.currentEnergy < 0.3) {
      minDesiredRings = 2; // Always at least 2 rings for low energy
    } else if (this.currentEnergy < 0.6) {
      minDesiredRings = 4; // At least 4 rings for medium energy
    } else if (this.currentEnergy < 0.8) {
      minDesiredRings = 8; // At least 8 rings for high energy
    } else {
      minDesiredRings = 12; // At least 12 rings for very high energy
    }
    
    // Multiple spawn conditions for continuous activity
    const shouldSpawnBeat = this.currentBeat > 0.7; // Strong beats always spawn
    const shouldSpawnBar = this.currentBar > 0.8; // Bar transitions spawn
    const shouldSpawnEnergy = this.rings.length < minDesiredRings; // Below minimum
    const shouldSpawnPeriodic = this.ringSpawnCooldown === 0; // Cooldown expired
    
    // Periodic spawning every 20-60 frames (depends on energy) for constant visual activity
    const periodicInterval = Math.floor(60 - this.currentEnergy * 40); // 20-60 frames
    const shouldSpawnTimed = this.frameCount % periodicInterval === 0;
    
    // Spawn if any condition is met and we haven't hit the max
    const shouldSpawn = (shouldSpawnBeat || shouldSpawnBar || shouldSpawnEnergy || 
                        (shouldSpawnPeriodic && shouldSpawnTimed)) && 
                        this.rings.length < dynamicMaxRings;
    
    if (shouldSpawn) {
      this.spawnEnergyRing(baseRadius, maxRadiusThreshold);
      
      // Set cooldown based on energy (higher energy = faster spawning)
      // Range: 3-15 frames between spawns
      const cooldownFrames = Math.max(3, Math.floor(15 - this.currentEnergy * 10));
      this.ringSpawnCooldown = shouldSpawnBeat ? 3 : cooldownFrames;
    }
  }
  
  /**
   * Spawn a new energy ring with properties based on current audio
   */
  private spawnEnergyRing(baseRadius: number, maxRadius: number): void {
    // Get dominant pitch for color
    const dominantPitchIndex = this.currentPitches.length > 0 
      ? this.currentPitches.indexOf(Math.max(...this.currentPitches))
      : Math.floor(Math.random() * 12);
    const pitchValue = this.currentPitches[dominantPitchIndex] || 0.5;
    
    // Speed proportional to energy (1.0 to 6 pixels per frame) - increased minimum
    const speed = 1.0 + this.currentEnergy * 5.0 + this.currentBeat * 2.0;
    
    // Thickness varies with loudness (2 to 10 pixels) - increased range
    const thickness = 2 + this.currentLoudness * 8;
    
    // Color influenced by dominant pitch and current hue offset
    const hue = (this.hueOffset + dominantPitchIndex * 30 + Math.random() * 40) % 360;
    const saturation = 75 + pitchValue * 25; // 75-100% saturation
    const lightness = 45 + this.currentEnergy * 25 + this.currentBeat * 10; // More dynamic
    
    // Starting radius varies for organic feel - can occasionally start larger
    const startRadius = baseRadius * (0.08 + Math.random() * 0.18);
    
    const ring: Ring = {
      radius: startRadius,
      maxRadius: maxRadius,
      speed: speed,
      thickness: thickness,
      opacity: 1,
      hue: hue,
      saturation: saturation,
      lightness: lightness,
      spawnTime: this.currentTime
    };
    
    this.rings.push(ring);
  }
  
  /**
   * Draw all active energy rings
   */
  private drawEnergyRings(centerX: number, centerY: number): void {
    // Draw rings from oldest to newest for proper layering
    for (const ring of this.rings) {
      // Apply glow effect for more psychedelic feel
      this.ctx.shadowBlur = 10 + ring.thickness;
      this.ctx.shadowColor = this.hsl(ring.hue, ring.saturation, ring.lightness, ring.opacity * 0.5);
      
      // Draw the ring
      this.ctx.strokeStyle = this.hsl(
        ring.hue,
        ring.saturation,
        ring.lightness,
        ring.opacity * 0.6
      );
      this.ctx.lineWidth = ring.thickness;
      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, ring.radius, 0, Math.PI * 2);
      this.ctx.stroke();
      
      // Add inner glow for more depth
      this.ctx.strokeStyle = this.hsl(
        ring.hue,
        ring.saturation,
        Math.min(90, ring.lightness + 20),
        ring.opacity * 0.3
      );
      this.ctx.lineWidth = ring.thickness * 0.5;
      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, ring.radius, 0, Math.PI * 2);
      this.ctx.stroke();
    }
    
    // Reset shadow
    this.ctx.shadowBlur = 0;
  }

  /**
   * Draw center orb with pulsing effect
   */
  private drawCenterOrb(centerX: number, centerY: number, scaleMultiplier: number = 1): void {
    const baseSize = this.getBaseRadius() * 0.15;
    const energyPulse = 1 + this.currentEnergy * 0.3;
    const beatPulse = this.currentBeat * 0.4;
    const sinePulse = Math.sin(this.pulsePhase) * 0.2;
    const pulseSize = baseSize * (energyPulse + sinePulse + beatPulse) * scaleMultiplier;
    
    // More outer glow layers for stronger effect
    const glowLayers = 4;
    for (let i = 0; i < glowLayers; i++) {
      const gradient = this.ctx.createRadialGradient(
        centerX, centerY, 0,
        centerX, centerY, pulseSize * (1 + i * 0.35)
      );
      
      const hue = (this.hueOffset + i * 30) % 360;
      const intensity = 0.9 - i * 0.2;
      gradient.addColorStop(0, this.hsl(hue, 100, 60, intensity));
      gradient.addColorStop(0.5, this.hsl(hue, 100, 50, intensity * 0.5));
      gradient.addColorStop(1, this.hsl(hue, 100, 40, 0));
      
      this.ctx.fillStyle = gradient;
      this.ctx.beginPath();
      this.ctx.arc(centerX, centerY, pulseSize * (1 + i * 0.35), 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    // Core with loudness-affected brightness
    const coreBrightness = 80 + this.currentLoudness * 15;
    const coreGradient = this.ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, pulseSize * 0.5
    );
    coreGradient.addColorStop(0, this.hsl(this.hueOffset, 100, coreBrightness, 1));
    coreGradient.addColorStop(1, this.hsl(this.hueOffset, 100, 60, 0.9));
    
    this.ctx.fillStyle = coreGradient;
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, pulseSize * 0.5, 0, Math.PI * 2);
    this.ctx.fill();
  }

  /**
   * Spawn particles on beat
   */
  private spawnBeatParticles(): void {
    const { x: centerX, y: centerY } = this.getCenter();
    const numParticles = Math.floor(8 + this.currentBeat * 20 + this.currentEnergy * 10);
    
    for (let i = 0; i < numParticles; i++) {
      if (this.particles.length >= this.maxParticles) break;
      
      const angle = (Math.PI * 2 * i) / numParticles + Math.random() * 0.3;
      const speed = 2 + this.currentEnergy * 4 + this.currentBeat * 2;
      const radius = this.getBaseRadius() * (0.3 + Math.random() * 0.4);
      
      // Use pitch data for color variety
      const pitchIndex = i % Math.max(1, this.currentPitches.length);
      const pitchValue = this.currentPitches[pitchIndex] || 0.5;
      
      this.particles.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        maxLife: 50 + Math.random() * 70 + pitchValue * 30,
        hue: (this.hueOffset + pitchIndex * 30 + Math.random() * 40) % 360,
        size: 2 + Math.random() * 4 + this.currentLoudness * 2
      });
    }
  }

  /**
   * Update and draw particle system
   */
  private updateAndDrawParticles(): void {
    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      
      p.x += p.vx;
      p.y += p.vy;
      p.life++;
      
      // Gravity and drag
      p.vy += 0.05;
      p.vx *= 0.98;
      p.vy *= 0.98;
      
      // Remove dead particles
      if (p.life > p.maxLife) {
        this.particles.splice(i, 1);
        continue;
      }
      
      // Draw particle with trail
      const alpha = 1 - (p.life / p.maxLife);
      this.ctx.fillStyle = this.hsl(p.hue, 100, 60, alpha * 0.8);
      this.ctx.shadowBlur = 10;
      this.ctx.shadowColor = this.hsl(p.hue, 100, 60, alpha);
      
      this.ctx.beginPath();
      this.ctx.arc(p.x, p.y, p.size * alpha, 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    this.ctx.shadowBlur = 0;
  }

  /**
   * Draw full-screen beat flash
   */
  private drawBeatFlash(): void {
    const alpha = this.currentBeat * 0.12;
    const hue = this.hueOffset;
    
    this.ctx.fillStyle = this.hsl(hue, 100, 70, alpha);
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }
  
  /**
   * Draw full-screen bar flash (different color)
   */
  private drawBarFlash(): void {
    const alpha = this.currentBar * 0.08;
    const hue = (this.hueOffset + 180) % 360; // Complementary color
    
    this.ctx.fillStyle = this.hsl(hue, 100, 60, alpha);
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /**
   * Override to add pitch-based variation to frequency data
   */
  protected generateSimulatedData(): void {
    super.generateSimulatedData();
    
    // Add extra variation based on current state
    for (let i = 0; i < this.simulatedData.length; i++) {
      const angle = (i / this.simulatedData.length) * Math.PI * 2;
      const wave = Math.sin(angle * 3 + this.currentTime * 3) * 20;
      const barWave = Math.cos(angle * 5 - this.currentTime * 2) * 15 * this.currentBar;
      const energyBoost = this.currentEnergy * 30; // Ensure minimum activity
      
      this.simulatedData[i] = Math.min(255, this.simulatedData[i] + wave + barWave + energyBoost);
    }
  }
  
  /**
   * Start intro animation when music begins playing
   */
  public startIntro(): void {
    if (this.visualizerState === 'running') {
      // Already running, just create a brief pulse effect
      this.createTrackChangePulse();
      return;
    }
    
    this.visualizerState = 'intro';
    this.introStartTime = this.currentTime;
    this.hasStartedOnce = true;
    
    console.log('🎵 Starting intro animation...');
  }
  
  /**
   * Check if visualizer is in intro state
   */
  public isInIntro(): boolean {
    return this.visualizerState === 'intro';
  }
  
  /**
   * Get intro progress (0-1)
   */
  public getIntroProgress(): number {
    if (this.visualizerState !== 'intro') return 1;
    
    const elapsed = this.currentTime - this.introStartTime;
    return Math.min(1, elapsed / this.introDuration);
  }
  
  /**
   * Create a brief pulse effect on track change
   */
  private createTrackChangePulse(): void {
    // Spawn multiple rings for dramatic effect
    const baseRadius = this.getBaseRadius();
    const maxRadius = Math.max(this.canvas.width, this.canvas.height) * 0.7;
    
    for (let i = 0; i < 5; i++) {
      setTimeout(() => {
        this.spawnEnergyRing(baseRadius, maxRadius);
      }, i * 100);
    }
  }
  
  /**
   * Render idle state (no music playing)
   */
  private renderIdleState(centerX: number, centerY: number): void {
    // Clear to black background
    this.ctx.fillStyle = 'rgba(0, 0, 5, 1)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    
    // Gentle pulsing center orb
    this.pulsePhase += 0.05;
    const baseSize = this.getBaseRadius() * 0.1;
    const pulse = Math.sin(this.pulsePhase) * 0.3 + 1;
    const orbSize = baseSize * pulse;
    
    // Slow color cycling
    this.hueOffset = (this.hueOffset + 0.3) % 360;
    
    // Draw minimal orb
    const gradient = this.ctx.createRadialGradient(
      centerX, centerY, 0,
      centerX, centerY, orbSize * 1.5
    );
    
    gradient.addColorStop(0, this.hsl(this.hueOffset, 80, 50, 0.4));
    gradient.addColorStop(0.5, this.hsl(this.hueOffset, 80, 40, 0.2));
    gradient.addColorStop(1, this.hsl(this.hueOffset, 80, 30, 0));
    
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, orbSize * 1.5, 0, Math.PI * 2);
    this.ctx.fill();
    
    // Core
    this.ctx.fillStyle = this.hsl(this.hueOffset, 90, 60, 0.6);
    this.ctx.beginPath();
    this.ctx.arc(centerX, centerY, orbSize * 0.5, 0, Math.PI * 2);
    this.ctx.fill();
    
    // Optional: Draw hint text if never started
    if (!this.hasStartedOnce) {
      this.ctx.save();
      this.ctx.fillStyle = this.hsl(this.hueOffset, 60, 70, 0.3 + Math.sin(this.pulsePhase * 0.5) * 0.1);
      this.ctx.font = '24px sans-serif';
      this.ctx.textAlign = 'center';
      this.ctx.textBaseline = 'middle';
      this.ctx.fillText('Play music to begin', centerX, centerY + this.getBaseRadius() * 0.4);
      this.ctx.restore();
    }
  }
  
  /**
   * Easing function: ease out cubic
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }
  
  /**
   * Easing function: ease out back (slight overshoot)
   */
  private easeOutBack(t: number): number {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  }
  
  /**
   * Debug method to log visualizer state (call from console)
   */
  public debugState(): void {
    console.log('=== PsychedelicVisualizer Debug State ===');
    console.log(`State: ${this.visualizerState} (Intro progress: ${this.getIntroProgress().toFixed(2)})`);
    console.log(`Rings: ${this.rings.length} (Cooldown: ${this.ringSpawnCooldown})`);
    console.log(`Particles: ${this.particles.length}/${this.maxParticles}`);
    console.log(`Audio - Beat: ${this.currentBeat.toFixed(2)}, Bar: ${this.currentBar.toFixed(2)}`);
    console.log(`Audio - Energy: ${this.currentEnergy.toFixed(2)}, Loudness: ${this.currentLoudness.toFixed(2)}`);
    console.log(`Pitches: [${this.currentPitches.map(p => p.toFixed(2)).join(', ')}]`);
    console.log(`Animation - Time: ${this.currentTime.toFixed(2)}, Frame: ${this.frameCount}`);
    console.log(`Animation - Hue: ${this.hueOffset.toFixed(0)}, Rotation: ${this.rotationOffset.toFixed(3)}`);
    console.log('=====================================');
  }
}
